import React, { useEffect, useMemo, useState } from "react";
import { Link, useLocation, useParams } from "react-router-dom";
import Alert from "../../../Common/Alert";
import Http from "../../../Common/Http";
import IsLogin from "../../../Common/IsLogin";
import ReactDataTable from "../../../Common/ReactDataTable";
import {
  COMPANY_NAME,
  ChartOfAccountStyles,
  firstDayOfMonth,
  formatDate,
  isOpeningRow,
  lastDayOfMonth,
  ledgerSummary,
  money,
  num,
} from "../Chart-Of-Account/ChartOfAccountShared";
import { typeLabel } from "./SubCategoryForm";

// The running balance is credit minus debit, so a positive figure is a
// credit balance.
const balanceSide = (value) => (value < 0 ? "DR" : "CR");

const Ledger = () => {
  const { id } = useParams();
  const location = useLocation();
  const [receiveData, setReceiveData] = useState([]);
  const [subCategory, setSubCategory] = useState({});
  const [alert, setAlert] = useState(null);
  const [inputs, setInputs] = useState({
    start_date: firstDayOfMonth(),
    end_date: lastDayOfMonth(),
  });
  const [loading, setLoading] = useState(true);

  const fetchData = async () => {
    setLoading(true);

    try {
      let url = `/report/sub-category-ledger?id=${id}`;
      if (inputs.start_date) {
        url += `&start_date=${inputs.start_date}`;
      }
      if (inputs.end_date) {
        url += `&end_date=${inputs.end_date}`;
      }

      const res = await Http.get(url);
      setReceiveData(res.data.list || []);
    } catch (error) {
      console.error("Fetch error:", error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();

    // The edit endpoint carries the parent account, which the ledger
    // response leaves out.
    Http.get("/sub-category/" + id + "/edit")
      .then((res) => setSubCategory(res.data || {}))
      .catch((e) => console.error("Fetch error:", e));
  }, [id]);

  const showAlert = (message, type) => {
    setAlert({
      message: message,
      type: type,
    });
    setTimeout(() => {
      setAlert(null);
    }, 4000);
  };

  useEffect(() => {
    if (!location.state) {
      return;
    }
    showAlert(location.state.message, location.state.type);
  }, [location]);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setInputs((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const summary = useMemo(
    () =>
      ledgerSummary(
        receiveData,
        (row) => num(row.debit),
        (row) => num(row.credit),
      ),
    [receiveData],
  );

  const rowBalance = (row) =>
    num(isOpeningRow(row) ? row.opening_balance : row.balance);

  const subCategoryName =
    subCategory.name === "new"
      ? subCategory.new_name
      : typeLabel(subCategory.name);
  const account = subCategory.chart_of_account
    ? `${subCategory.chart_of_account.code} - ${subCategory.chart_of_account.name}`
    : "";

  const dateBodyTemplate = (rowData) =>
    isOpeningRow(rowData) ? (
      <span style={{ fontWeight: 600, color: "#2a2962" }}>Opening balance</span>
    ) : (
      <span style={{ whiteSpace: "nowrap" }}>{formatDate(rowData.date)}</span>
    );

  const plainDateBodyTemplate = (field) => {
    const Component = (rowData) =>
      rowData[field] ? (
        <span style={{ whiteSpace: "nowrap" }}>
          {formatDate(rowData[field])}
        </span>
      ) : (
        <span style={{ color: "#b6bacb" }}>—</span>
      );
    Component.displayName = `DateBodyTemplate(${field})`;
    return Component;
  };

  const textBodyTemplate = (field) => {
    const Component = (rowData) =>
      rowData[field] ? (
        <span>{rowData[field]}</span>
      ) : (
        <span style={{ color: "#b6bacb" }}>—</span>
      );
    Component.displayName = `TextBodyTemplate(${field})`;
    return Component;
  };

  const serialBodyTemplate = (rowData) =>
    rowData.serial_number ? (
      <span className="coa-code">{rowData.serial_number}</span>
    ) : (
      <span style={{ color: "#b6bacb" }}>—</span>
    );

  const amountBodyTemplate = (field) => {
    const Component = (rowData) => {
      const value = num(rowData[field]);
      return (
        <span className="coa-num" style={{ display: "block" }}>
          {value ? money(value) : <span style={{ color: "#dfe2ec" }}>—</span>}
        </span>
      );
    };
    Component.displayName = `AmountBodyTemplate(${field})`;
    return Component;
  };

  const balanceBodyTemplate = (rowData) => {
    const value = rowBalance(rowData);
    return (
      <span
        className="coa-num"
        style={{ display: "block", fontWeight: 700, color: "#2a2962" }}
      >
        {money(Math.abs(value))}
        <span
          className={`coa-pill mg-l-6 ${
            value < 0 ? "coa-pill-dr" : "coa-pill-cr"
          }`}
        >
          {balanceSide(value)}
        </span>
      </span>
    );
  };

  const cols = [
    { field: "date", header: "Date", body: dateBodyTemplate },
    { field: "serial_number", header: "Voucher No.", body: serialBodyTemplate },
    {
      field: "chart_of_account",
      header: "Account",
      body: textBodyTemplate("chart_of_account"),
    },
    {
      field: "subsidiary",
      header: "Party",
      body: textBodyTemplate("subsidiary"),
    },
    {
      field: "bill_number",
      header: "Bill No.",
      body: textBodyTemplate("bill_number"),
    },
    {
      field: "bill_date",
      header: "Bill Date",
      body: plainDateBodyTemplate("bill_date"),
    },
    {
      field: "mature_day",
      header: "Mature Date",
      body: plainDateBodyTemplate("mature_day"),
    },
    {
      field: "debit",
      header: "Debit",
      body: amountBodyTemplate("debit"),
      headerClassName: "coa-num",
    },
    {
      field: "credit",
      header: "Credit",
      body: amountBodyTemplate("credit"),
      headerClassName: "coa-num",
    },
    {
      field: "balance",
      header: "Balance",
      sortable: false,
      body: balanceBodyTemplate,
      headerClassName: "coa-num",
    },
  ];

  const handlePrint = () => {
    const printWindow = window.open("", "_blank");
    const amount = (value) => (num(value) ? money(value) : "");
    const printContent = `
      <!DOCTYPE html>
      <html>
      <head>
        <title>Sub Category Ledger Report</title>
        <style>
          body { font-family: Arial, sans-serif; margin: 20px; color: #22242f; }
          .header { text-align: center; margin-bottom: 24px; border-bottom: 2px solid #2a2962; padding-bottom: 14px; }
          .company { margin: 0; font-size: 22px; font-weight: bold; color: #2a2962; }
          .account { margin: 8px 0 4px; font-size: 16px; }
          .period { margin: 0; font-size: 13px; color: #555; }
          table { width: 100%; border-collapse: collapse; margin-top: 18px; font-size: 11px; }
          th, td { border: 1px solid #ddd; padding: 6px 8px; text-align: left; }
          th { background-color: #2a2962; color: #fff; font-weight: bold; text-transform: uppercase; font-size: 10px; }
          tfoot td { background-color: #f2f3f9; font-weight: bold; }
          .text-right { text-align: right; font-variant-numeric: tabular-nums; }
          .text-center { text-align: center; }
        </style>
      </head>
      <body>
        <div class="header">
          <h1 class="company">${COMPANY_NAME}</h1>
          <h3 class="account">${subCategoryName || "Sub Category"}${
            account ? ` — ${account}` : ""
          }</h3>
          <p class="period">Sub category statement from ${inputs.start_date} to ${inputs.end_date}</p>
        </div>
        <table>
          <thead>
            <tr>${cols.map((col) => `<th>${col.header}</th>`).join("")}</tr>
          </thead>
          <tbody>
            ${receiveData
              .map((row) => {
                const balance = rowBalance(row);
                return `
              <tr>
                <td class="text-center">${
                  isOpeningRow(row) ? "Opening balance" : row.date || ""
                }</td>
                <td class="text-center">${row.serial_number || ""}</td>
                <td>${row.chart_of_account || ""}</td>
                <td>${row.subsidiary || ""}</td>
                <td>${row.bill_number || ""}</td>
                <td class="text-center">${row.bill_date || ""}</td>
                <td class="text-center">${row.mature_day || ""}</td>
                <td class="text-right">${amount(row.debit)}</td>
                <td class="text-right">${amount(row.credit)}</td>
                <td class="text-right">${money(Math.abs(balance))} ${balanceSide(balance)}</td>
              </tr>`;
              })
              .join("")}
          </tbody>
          <tfoot>
            <tr>
              <td colspan="7" class="text-right">Period total</td>
              <td class="text-right">${money(summary.debit)}</td>
              <td class="text-right">${money(summary.credit)}</td>
              <td class="text-right">${money(Math.abs(summary.closing))} ${balanceSide(summary.closing)}</td>
            </tr>
          </tfoot>
        </table>
        <script>
          window.onload = function() {
            window.print();
            window.onafterprint = function() { window.close(); }
          }
        </script>
      </body>
      </html>
    `;

    printWindow.document.write(printContent);
    printWindow.document.close();
  };

  return (
    <div>
      <IsLogin></IsLogin>
      <div className="am-mainpanel">
        <ChartOfAccountStyles />
        <div className="am-pagetitle">
          <div>
            <h5 className="am-title mb-1">
              Sub Category Ledger{" "}
              {subCategoryName && (
                <span className="coa-pill coa-pill-dr">{subCategoryName}</span>
              )}
            </h5>
            <p className="text-muted mb-0 tx-12">
              {account || `Sub category #${id}`} &middot;{" "}
              {formatDate(inputs.start_date)} — {formatDate(inputs.end_date)}
            </p>
          </div>
          <div className="d-flex gap-2 coa-no-print">
            <Link to="/sub-category" className="btn btn-outline-secondary">
              <i className="fa fa-arrow-left mg-r-6"></i>Back to List
            </Link>
            <Link
              to={`/edit-sub-category/${id}`}
              className="btn btn-outline-secondary"
            >
              <i className="fa fa-pencil mg-r-6"></i>Edit
            </Link>
            <button
              className="btn btn-primary"
              onClick={handlePrint}
              disabled={loading || !receiveData.length}
            >
              <i className="fa fa-print mg-r-6"></i>Print
            </button>
          </div>
        </div>

        <div className="am-pagebody">
          <Alert alert={alert} />

          <div className="coa-panel">
            <div className="coa-panel-head">
              <span>
                <i className="fa fa-calendar-o me-2"></i>Statement Period
              </span>
              <span className="coa-head-note">
                {summary.count} {summary.count === 1 ? "entry" : "entries"} in
                range
              </span>
            </div>
            <div className="coa-panel-body">
              <div className="row align-items-end">
                <div className="col-lg-4">
                  <label>Start Date</label>
                  <input
                    type="date"
                    className="form-control"
                    name="start_date"
                    value={inputs.start_date}
                    onChange={handleInputChange}
                    max={inputs.end_date}
                  />
                </div>
                <div className="col-lg-4 mg-t-10 mg-lg-t-0">
                  <label>End Date</label>
                  <input
                    type="date"
                    className="form-control"
                    name="end_date"
                    value={inputs.end_date}
                    onChange={handleInputChange}
                    min={inputs.start_date}
                  />
                </div>
                <div className="col-lg-4 mg-t-10 mg-lg-t-0">
                  <button
                    className="btn btn-primary"
                    onClick={fetchData}
                    disabled={loading}
                  >
                    <i
                      className={`fa ${
                        loading ? "fa-spinner fa-spin" : "fa-search"
                      } mg-r-6`}
                    ></i>
                    {loading ? "Loading..." : "Apply"}
                  </button>
                </div>
              </div>
            </div>
          </div>

          <div className="row mg-b-20">
            <div className="col-md-3 mg-b-10">
              <div className="coa-stat">
                <i className="fa fa-flag-o coa-stat-icon"></i>
                <span className="coa-stat-label">Opening Balance</span>
                <span className="coa-stat-value">
                  {money(Math.abs(summary.opening))}{" "}
                  <small>{balanceSide(summary.opening)}</small>
                </span>
                <span className="coa-stat-sub">
                  As at {formatDate(inputs.start_date)}
                </span>
              </div>
            </div>
            <div className="col-md-3 mg-b-10">
              <div className="coa-stat">
                <i className="fa fa-arrow-circle-o-down coa-stat-icon"></i>
                <span className="coa-stat-label">Total Debit</span>
                <span className="coa-stat-value">{money(summary.debit)}</span>
                <span className="coa-stat-sub">Booked in this period</span>
              </div>
            </div>
            <div className="col-md-3 mg-b-10">
              <div className="coa-stat">
                <i className="fa fa-arrow-circle-o-up coa-stat-icon"></i>
                <span className="coa-stat-label">Total Credit</span>
                <span className="coa-stat-value">{money(summary.credit)}</span>
                <span className="coa-stat-sub">Booked in this period</span>
              </div>
            </div>
            <div className="col-md-3 mg-b-10">
              <div className="coa-stat">
                <i className="fa fa-balance-scale coa-stat-icon"></i>
                <span className="coa-stat-label">Closing Balance</span>
                <span className="coa-stat-value">
                  {money(Math.abs(summary.closing))}{" "}
                  <small>{balanceSide(summary.closing)}</small>
                </span>
                <span className="coa-stat-sub">
                  As at {formatDate(inputs.end_date)}
                </span>
              </div>
            </div>
          </div>

          <div className="coa-panel">
            <div className="coa-panel-head">
              <span>
                <i className="fa fa-table me-2"></i>Ledger Entries
              </span>
            </div>
            <div className="coa-panel-body">
              <ReactDataTable
                cols={cols}
                receiveData={receiveData}
                isLoading={loading}
              />
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Ledger;
